import React from "react";
import {
  View,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  Image as RNImage,
  Pressable,
  Platform,
  useWindowDimensions,
} from "react-native";
import { TTemplate } from "../../../../../../utils/types";
import { SessionContext } from "../../../../../../contexts";
import { MAX_ZINDEX } from "../../../../../../utils/constant";
import Copy from "../../../../../../assets/icons/Copy.icon";
import TrashCan from "../../../../../../assets/icons/TrashCan.icon";
import {
  MAX_FILE_SIZE,
  VALID_IMAGE_TYPES,
} from "../../../../../common/web/MultiImageUpload.web";
import { useClinicAssets } from "../../../../../common/hooks/useClinicAssets";
import {
  InputPatientDetailDropdown,
  NormalText,
  SecondaryButton,
} from "../../../../../common/native";
import { STICKER_PRESET_URLS } from "../../../../../../config/stickerPresets";

type AddImageModalContentProps = {
  currentTemplate: TTemplate;
  currentImageEditingIndex: number | null;
  setCurrentTemplate: (template: TTemplate) => void;
  onSetCurrentImageEditingIndex?: (index: number | null) => void;
  onDeleteImage?: () => void;
  onDuplicateImage?: () => void;
};

type AddImageModalView = "main" | "existing_assets" | "stickers";

const STICKER_MAX_SIDE = 100;
const TOKENS = {
  color: {
    textPrimary: "#282B33",
    borderSubtle: "#C4C6CD",
    surfaceMuted: "#F8F8F8",
    accent: "#00B7F4",
    inverseText: "#FFFFFF",
    danger: "#B42318",
    focusRing: "#00759B",
  },
  spacing: {
    s4: 4,
    s6: 6,
    s8: 8,
    s10: 10,
    s12: 12,
  },
  radius: {
    r8: 8,
  },
  shadow: {
    none: {},
  },
  font: {
    regular: "Urbanist400",
    semibold: "Urbanist700",
  },
  fontSize: {
    s12: 12,
    s14: 14,
  },
  lineHeight: {
    s12: 12 * 1.4,
    s14: 14 * 1.4,
  },
  zIndex: {
    dropdown: 1,
  },
  motion: {
    micro: 100,
    standard: 200,
  },
};

const AddImageModalContent: React.FC<AddImageModalContentProps> = ({
  currentTemplate,
  setCurrentTemplate,
  currentImageEditingIndex,
  onSetCurrentImageEditingIndex,
  onDeleteImage,
  onDuplicateImage,
}) => {
  // Keep newly inserted images visually manageable on first render.
  const DEFAULT_IMAGE_MAX_SIDE = 240;
  const DEFAULT_IMAGE_MIN_SIDE = 80;

  // Reads intrinsic dimensions from the local file before upload so we can
  // preserve aspect ratio when creating the initial template image box.
  const getImageDimensions = (
    file: File,
  ): Promise<{ width: number; height: number }> =>
    new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();

      img.onload = () => {
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        URL.revokeObjectURL(objectUrl);
        resolve({ width, height });
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Failed to read image dimensions."));
      };

      img.src = objectUrl;
    });

  const getImageDimensionsFromUrl = (
    src: string,
  ): Promise<{ width: number; height: number }> =>
    new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => {
        resolve({
          width: img.naturalWidth || 100,
          height: img.naturalHeight || 100,
        });
      };
      img.onerror = () => {
        reject(new Error("Failed to read image dimensions from URL."));
      };
      img.src = src;
    });

  // Scales the image proportionally into a default editable size window.
  // Large images are reduced to max side; very small images are increased to
  // min side to avoid tiny drag handles in the editor.
  const getDefaultImageSize = (width: number, height: number) => {
    if (width <= 0 || height <= 0) {
      return { width: 100, height: 100 };
    }

    const longestSide = Math.max(width, height);
    const shortestSide = Math.min(width, height);

    let scale = 1;

    if (longestSide > DEFAULT_IMAGE_MAX_SIDE) {
      scale = DEFAULT_IMAGE_MAX_SIDE / longestSide;
    } else if (shortestSide < DEFAULT_IMAGE_MIN_SIDE) {
      scale = DEFAULT_IMAGE_MIN_SIDE / shortestSide;
    }

    return {
      width: Math.round(width * scale),
      height: Math.round(height * scale),
    };
  };

  const getStickerImageSize = (width: number, height: number) => {
    if (width <= 0 || height <= 0) {
      return { width: 100, height: 100 };
    }

    const longestSide = Math.max(width, height);
    const scale =
      longestSide > STICKER_MAX_SIDE ? STICKER_MAX_SIDE / longestSide : 1;

    return {
      width: Math.round(width * scale),
      height: Math.round(height * scale),
    };
  };

  const session = React.useContext(SessionContext)?.session;
  const {
    assets: existingAssets,
    isLoading: loadingExistingAssets,
    deletingAssetId,
    load: loadExistingAssets,
    upload: uploadClinicAsset,
    remove: removeClinicAsset,
  } = useClinicAssets({ session });
  if (!session) return <p>Must have a session</p>;

  const [objectFit, setObjectFit] = React.useState<string>("contain");
  const [preserveAspectRatio, setPreserveAspectRatio] =
    React.useState<boolean>(false);
  const [modalView, setModalView] = React.useState<AddImageModalView>("main");
  const [existingAssetsError, setExistingAssetsError] =
    React.useState<string>("");
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");
  const [focusedAssetKey, setFocusedAssetKey] = React.useState<string | null>(
    null,
  );
  const [isBackLinkFocused, setIsBackLinkFocused] =
    React.useState<boolean>(false);
  const { width: windowWidth } = useWindowDimensions();
  const [assetToDelete, setAssetToDelete] = React.useState<{
    assetId: number;
    src: string;
  } | null>(null);

  const currentImageBeingEdited =
    currentImageEditingIndex !== null
      ? currentTemplate?.images?.[currentImageEditingIndex]
      : null;
  const isEditingCensorshipImage =
    currentImageBeingEdited?.kind === "censorship";
  const showDesktopShortcuts = Platform.OS === "web" && windowWidth >= 1024;

  const handleInsertImageFromUrl = React.useCallback(
    async (
      src: string,
      assetId?: number,
      preferredDimensions?: { width: number; height: number },
      isSticker?: boolean,
    ) => {
      let defaultImageSize = { width: 100, height: 100 };

      if (preferredDimensions) {
        defaultImageSize = isSticker
          ? getStickerImageSize(
              preferredDimensions.width,
              preferredDimensions.height,
            )
          : getDefaultImageSize(
              preferredDimensions.width,
              preferredDimensions.height,
            );
      } else {
        try {
          const { width, height } = await getImageDimensionsFromUrl(src);
          defaultImageSize = isSticker
            ? getStickerImageSize(width, height)
            : getDefaultImageSize(width, height);
        } catch (dimensionError) {
          // Fall back to default size when URL dimensions cannot be read.
        }
      }

      let newImageIndex: number | null = null;
      // @ts-ignore
      setCurrentTemplate((prev) => {
        if (!prev) return prev;
        newImageIndex = prev.images.length;
        return {
          ...prev,
          images: [
            ...prev.images,
            {
              src,
              assetId,
              position: { x: 250, y: 250 },
              size: {
                height: defaultImageSize.height,
                width: defaultImageSize.width,
              },
              containerStyle: {
                // IMPORTANT NOTE for template creator: DO NOT use use zIndex
                // in the range (MAX_ZINDEX, MAX_ZINDEX + 10)
                // for images or texts, as the range is reserved for coder.
                zIndex: MAX_ZINDEX - 9,
              },
              imageStyle: {
                width: "100%",
                height: "100%",
                objectFit: "contain",
              },
              preserveAspectRatio: false,
            },
          ],
        };
      });

      if (newImageIndex !== null) {
        onSetCurrentImageEditingIndex?.(newImageIndex);
      }
    },
    [onSetCurrentImageEditingIndex, setCurrentTemplate],
  );

  const handleUploadImage = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setError("");
    setLoading(true);
    const files = event.target.files;
    if (!files || files.length === 0) {
      setLoading(false);
      return;
    }

    try {
      if (!VALID_IMAGE_TYPES.includes(files[0].type)) {
        setError("Invalid file type, only PNG and JPEG are allowed.");
        throw new Error("Invalid file type, only PNG and JPEG are allowed.");
      }

      if (files[0].size > MAX_FILE_SIZE) {
        throw new Error("File size should not exceed 50MB.");
      }

      if (!session) throw new Error("Session is not available");

      const { width, height } = await getImageDimensions(files[0]);

      const response = await uploadClinicAsset(files[0]);
      await handleInsertImageFromUrl(response.get_url, response.asset_id, {
        width,
        height,
      });
    } catch (error) {
      setError(`${error}`);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (currentImageEditingIndex === null) return;
    const imageToEdit = currentTemplate?.images?.[currentImageEditingIndex];
    if (!imageToEdit) return;

    const nextObjectFit =
      typeof imageToEdit.imageStyle.objectFit === "string"
        ? imageToEdit.imageStyle.objectFit
        : "contain";
    const nextPreserveAspectRatio = !!imageToEdit.preserveAspectRatio;

    setObjectFit((prev) => (prev === nextObjectFit ? prev : nextObjectFit));
    setPreserveAspectRatio((prev) =>
      prev === nextPreserveAspectRatio ? prev : nextPreserveAspectRatio,
    );
  }, [currentImageEditingIndex]);

  const fetchExistingAssets = React.useCallback(async () => {
    setExistingAssetsError("");
    try {
      await loadExistingAssets();
    } catch {
      setExistingAssetsError(
        "Unable to load clinic assets. Try again or upload from computer.",
      );
    }
  }, [loadExistingAssets]);

  React.useEffect(() => {
    if (modalView !== "existing_assets") return;
    fetchExistingAssets();
  }, [fetchExistingAssets, modalView]);

  React.useEffect(() => {
    if (currentImageEditingIndex === null) return;
    setModalView("main");
  }, [currentImageEditingIndex]);

  const handleReplaceCurrentImageSrc = React.useCallback(
    (src: string, assetId?: number) => {
      if (currentImageEditingIndex === null) return;
      // @ts-ignore
      setCurrentTemplate((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          images: prev.images.map((image: any, index: number) =>
            index === currentImageEditingIndex
              ? {
                  ...image,
                  src,
                  assetId,
                  ...(image.kind === "censorship"
                    ? { isUserAdjusted: true }
                    : {}),
                }
              : image,
          ),
        };
      });
      setModalView("main");
    },
    [currentImageEditingIndex, setCurrentTemplate],
  );

  React.useEffect(() => {
    // @ts-ignore
    setCurrentTemplate((prev) => {
      if (
        !prev ||
        prev.images.length === 0 ||
        currentImageEditingIndex === null
      ) {
        return prev;
      }

      const currentImage = prev.images[currentImageEditingIndex];
      if (!currentImage) return prev;

      const currentObjectFit = String(currentImage.imageStyle?.objectFit || "");
      const currentPreserveAspectRatio = !!currentImage.preserveAspectRatio;

      if (
        currentObjectFit === objectFit &&
        currentPreserveAspectRatio === preserveAspectRatio
      ) {
        return prev;
      }

      return {
        ...prev,
        images: prev.images.map((image: any, index: number) =>
          index === currentImageEditingIndex
            ? {
                ...image,
                imageStyle: {
                  ...image.imageStyle,
                  objectFit: objectFit,
                },
                preserveAspectRatio,
                ...(image.kind === "censorship"
                  ? { isUserAdjusted: true }
                  : {}),
              }
            : image,
        ),
      };
    });
  }, [currentImageEditingIndex, objectFit, preserveAspectRatio]);

  const renderBackHeader = (title: string) => (
    <View style={styles.backHeader}>
      <Pressable
        onPress={() => setModalView("main")}
        onFocus={() => setIsBackLinkFocused(true)}
        onBlur={() => setIsBackLinkFocused(false)}
        accessibilityRole="button"
        accessibilityLabel="Back to image options"
        style={({ pressed }) => [
          styles.backLink,
          isBackLinkFocused && styles.backLinkFocused,
          pressed && styles.backLinkPressed,
        ]}
      >
        <NormalText text="← Back" style={styles.backLinkText} />
      </Pressable>
      <NormalText text={title} style={styles.viewTitle} />
    </View>
  );

  const handleDeleteAsset = React.useCallback(
    async (assetId: number) => {
      try {
        await removeClinicAsset(assetId);
        setAssetToDelete(null);
      } catch (error) {
        setError(`Failed to delete asset: ${String(error)}`);
      }
    },
    [removeClinicAsset],
  );

  const renderAssetGrid = (
    items: Array<{ key: string; src: string; assetId?: number }>,
    onSelect: (item: { key: string; src: string; assetId?: number }) => void,
    showDelete: boolean = false,
  ) => (
    <ScrollView
      style={styles.assetScroll}
      contentContainerStyle={styles.assetGrid}
    >
      {items.map((item) => (
        <View key={item.key} style={styles.assetCardWrapper}>
          <Pressable
            style={[
              styles.assetCard,
              focusedAssetKey === item.key && styles.assetCardFocused,
              loading && styles.assetCardDisabled,
            ]}
            onPress={() => onSelect(item)}
            onFocus={() => setFocusedAssetKey(item.key)}
            onBlur={() =>
              setFocusedAssetKey((prev) => (prev === item.key ? null : prev))
            }
            disabled={loading}
            accessibilityRole="button"
            accessibilityLabel="Add asset to template"
          >
            <RNImage source={{ uri: item.src }} style={styles.assetImage} />
            {showDelete && item.assetId && (
              <Pressable
                style={styles.deleteButton}
                onPress={(e) => {
                  if (Platform.OS === "web") {
                    (e as any).stopPropagation();
                  }
                  setAssetToDelete({ assetId: item.assetId!, src: item.src });
                }}
                disabled={deletingAssetId === item.assetId}
                accessibilityRole="button"
                accessibilityLabel="Delete asset"
              >
                <View style={styles.deleteIconContainer}>
                  {deletingAssetId === item.assetId ? (
                    <ActivityIndicator
                      size="small"
                      color={TOKENS.color.inverseText}
                    />
                  ) : (
                    <TrashCan color={TOKENS.color.inverseText} />
                  )}
                </View>
              </Pressable>
            )}
          </Pressable>
        </View>
      ))}
    </ScrollView>
  );

  const renderAddModeContent = () => {
    if (modalView === "existing_assets") {
      return (
        <View style={styles.subviewContainer}>
          {renderBackHeader("Add Existing Asset")}
          {loadingExistingAssets && (
            <ActivityIndicator size="large" color={TOKENS.color.accent} />
          )}
          {!loadingExistingAssets && existingAssetsError && (
            <View style={styles.feedbackContainer}>
              <NormalText text={existingAssetsError} style={styles.errorText} />
              <SecondaryButton
                text="Retry"
                onPress={fetchExistingAssets}
                width={140}
                textStyle={{
                  fontSize: TOKENS.fontSize.s12,
                  lineHeight: TOKENS.lineHeight.s12,
                }}
              />
              <SecondaryButton
                text="Upload from computer"
                onPress={() => fileInputRef.current?.click()}
                width={200}
                textStyle={{
                  fontSize: TOKENS.fontSize.s12,
                  lineHeight: TOKENS.lineHeight.s12,
                }}
              />
            </View>
          )}
          {!loadingExistingAssets &&
            !existingAssetsError &&
            existingAssets.length === 0 && (
              <View style={styles.feedbackContainer}>
                <NormalText text="No uploaded assets found. Upload from computer to get started." />
                <SecondaryButton
                  text="Upload from computer"
                  onPress={() => fileInputRef.current?.click()}
                  width={200}
                  textStyle={{
                    fontSize: TOKENS.fontSize.s12,
                    lineHeight: TOKENS.lineHeight.s12,
                  }}
                />
              </View>
            )}
          {!loadingExistingAssets &&
            !existingAssetsError &&
            existingAssets.length > 0 &&
            renderAssetGrid(
              existingAssets.map((asset) => ({
                key: String(asset.asset_id),
                src: asset.get_url,
                assetId: asset.asset_id,
              })),
              async (item) => {
                setLoading(true);
                setError("");
                try {
                  await handleInsertImageFromUrl(item.src, item.assetId);
                } catch (insertError) {
                  setError(
                    `Failed to add selected asset. ${String(insertError)}`,
                  );
                } finally {
                  setLoading(false);
                }
              },
              true,
            )}
        </View>
      );
    }

    if (modalView === "stickers") {
      return (
        <View style={styles.subviewContainer}>
          {renderBackHeader("Add Sticker")}
          <NormalText text="Stickers" style={styles.sectionTitle} />
          {renderAssetGrid(
            STICKER_PRESET_URLS.map((url) => ({ key: url, src: url })),
            async (item) => {
              setLoading(true);
              setError("");
              try {
                await handleInsertImageFromUrl(
                  item.src,
                  undefined,
                  undefined,
                  true,
                );
              } catch (insertError) {
                setError(`Failed to add sticker. ${String(insertError)}`);
              } finally {
                setLoading(false);
              }
            },
          )}
        </View>
      );
    }

    return (
      <View style={styles.mainOptionsContainer}>
        <NormalText text="Image Options" style={styles.mainTitle} />
        <SecondaryButton
          text={loading ? "Uploading..." : "Upload from computer"}
          onPress={() => fileInputRef.current?.click()}
          disabled={loading}
          width={260}
          textStyle={{
            fontSize: TOKENS.fontSize.s12,
            lineHeight: TOKENS.lineHeight.s12,
          }}
        />
        <SecondaryButton
          text="Select existing asset"
          onPress={() => setModalView("existing_assets")}
          disabled={loading}
          width={260}
          textStyle={{
            fontSize: TOKENS.fontSize.s12,
            lineHeight: TOKENS.lineHeight.s12,
          }}
        />
        <SecondaryButton
          text="Add sticker"
          onPress={() => setModalView("stickers")}
          disabled={loading}
          width={260}
          textStyle={{
            fontSize: TOKENS.fontSize.s12,
            lineHeight: TOKENS.lineHeight.s12,
          }}
        />
      </View>
    );
  };

  const renderDeleteConfirmationDialog = () => {
    if (!assetToDelete) return null;

    return (
      <View style={styles.dialogOverlay}>
        <View style={styles.dialogContainer}>
          <NormalText text="Delete Asset?" style={styles.dialogTitle} />
          <View style={styles.dialogImagePreview}>
            <RNImage
              source={{ uri: assetToDelete.src }}
              style={styles.dialogImage}
            />
          </View>
          <NormalText
            text="This will permanently delete this asset from your clinic. This cannot be undone."
            style={styles.dialogMessage}
          />
          <View style={styles.dialogButtons}>
            <SecondaryButton
              text="Cancel"
              onPress={() => setAssetToDelete(null)}
              width={120}
              textStyle={{
                fontSize: TOKENS.fontSize.s12,
                lineHeight: TOKENS.lineHeight.s12,
              }}
            />
            <SecondaryButton
              text="Delete"
              onPress={() => handleDeleteAsset(assetToDelete.assetId)}
              width={120}
              style={{
                backgroundColor: TOKENS.color.danger,
                borderColor: TOKENS.color.danger,
              }}
              textStyle={{
                fontSize: TOKENS.fontSize.s12,
                lineHeight: TOKENS.lineHeight.s12,
                color: TOKENS.color.inverseText,
              }}
            />
          </View>
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {currentImageEditingIndex !== null &&
      modalView === "stickers" &&
      isEditingCensorshipImage ? (
        <View style={styles.subviewContainer}>
          {renderBackHeader("Switch Sticker")}
          <NormalText text="Stickers" style={styles.sectionTitle} />
          {renderAssetGrid(
            STICKER_PRESET_URLS.map((url) => ({ key: url, src: url })),
            (item) => {
              setError("");
              handleReplaceCurrentImageSrc(item.src);
            },
          )}
        </View>
      ) : currentImageEditingIndex !== null ? (
        <>
          <View
            style={[
              styles.row,
              {
                zIndex: TOKENS.zIndex.dropdown,
                marginTop: TOKENS.spacing.s10,
                width: 260,
              },
            ]}
          >
            <InputPatientDetailDropdown
              name="Image Fit Mode"
              value={[objectFit]}
              selections={["fill", "contain", "cover"]}
              textWidth={130}
              fontSize={TOKENS.fontSize.s12}
              containerStyle={{ width: 260 }}
              onSelect={(value: (string | number)[]) =>
                setObjectFit(value[0] as string)
              }
            />
          </View>
          <View style={[styles.row, { width: 260 }]}>
            <NormalText
              text="Preserve Aspect Ratio:"
              style={{
                fontSize: TOKENS.fontSize.s12,
                lineHeight: TOKENS.lineHeight.s12,
              }}
            />
            <SecondaryButton
              text={preserveAspectRatio ? "On" : "Off"}
              onPress={() => setPreserveAspectRatio((prev) => !prev)}
              width={70}
              style={
                preserveAspectRatio
                  ? {
                      backgroundColor: TOKENS.color.accent,
                      borderColor: TOKENS.color.accent,
                    }
                  : undefined
              }
              textStyle={{
                fontSize: TOKENS.fontSize.s12,
                lineHeight: TOKENS.lineHeight.s12,
                ...(preserveAspectRatio
                  ? { color: TOKENS.color.inverseText }
                  : {}),
              }}
            />
          </View>
          {isEditingCensorshipImage && (
            <View style={[styles.row, { width: 260 }]}>
              <NormalText
                text="Sticker:"
                style={{
                  fontSize: TOKENS.fontSize.s12,
                  lineHeight: TOKENS.lineHeight.s12,
                }}
              />
              <SecondaryButton
                text="Switch"
                onPress={() => setModalView("stickers")}
                width={120}
                textStyle={{
                  fontSize: TOKENS.fontSize.s12,
                  lineHeight: TOKENS.lineHeight.s12,
                }}
              />
            </View>
          )}
          {onDuplicateImage && (
            <View style={[styles.row, { width: 260 }]}>
              <View style={styles.actionLabelContainer}>
                <NormalText
                  text="Duplicate Image:"
                  style={{
                    fontSize: TOKENS.fontSize.s12,
                    lineHeight: TOKENS.lineHeight.s12,
                  }}
                />
                {showDesktopShortcuts && (
                  <NormalText
                    text={"Ctrl + C (Windows)\nCmd + C (Mac)"}
                    style={styles.shortcutText}
                  />
                )}
              </View>
              <View style={styles.actionButtonContainer}>
                <SecondaryButton
                  text="Duplicate"
                  onPress={onDuplicateImage}
                  icon={<Copy color={TOKENS.color.textPrimary} />}
                  width={120}
                  textStyle={{
                    fontSize: TOKENS.fontSize.s12,
                    lineHeight: TOKENS.lineHeight.s12,
                  }}
                />
              </View>
            </View>
          )}
          {onDeleteImage && (
            <View style={[styles.row, { width: 260 }]}>
              <View style={styles.actionLabelContainer}>
                <NormalText
                  text="Delete Image:"
                  style={{
                    fontSize: TOKENS.fontSize.s12,
                    lineHeight: TOKENS.lineHeight.s12,
                  }}
                />
                {showDesktopShortcuts && (
                  <NormalText
                    text="Del / Backspace"
                    style={styles.shortcutText}
                  />
                )}
              </View>
              <SecondaryButton
                text="Delete"
                onPress={onDeleteImage}
                width={120}
                textStyle={{
                  fontSize: TOKENS.fontSize.s12,
                  lineHeight: TOKENS.lineHeight.s12,
                }}
              />
            </View>
          )}
        </>
      ) : (
        renderAddModeContent()
      )}

      {loading && (
        <ActivityIndicator size="large" color={TOKENS.color.accent} />
      )}

      {error && <NormalText text={error} style={styles.errorText} />}

      <input
        type="file"
        id="imageInput"
        accept="image/png, image/jpeg, image/jpg"
        onChange={handleUploadImage}
        className="hidden-file-input"
        ref={fileInputRef}
      />

      {renderDeleteConfirmationDialog()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: TOKENS.spacing.s6,
    flex: 1,
    alignItems: "center",
    width: "100%",
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: TOKENS.spacing.s6,
    width: 220,
  },
  actionLabelContainer: {
    alignItems: "flex-start",
    gap: TOKENS.spacing.s4,
    width: 120,
  },
  actionButtonContainer: {
    width: 120,
    alignItems: "flex-start",
  },
  shortcutText: {
    color: TOKENS.color.textPrimary,
    fontSize: TOKENS.fontSize.s12,
    lineHeight: TOKENS.lineHeight.s12,
  },
  mainOptionsContainer: {
    width: "100%",
    alignItems: "center",
    gap: TOKENS.spacing.s10,
    paddingTop: TOKENS.spacing.s10,
  },
  mainTitle: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.s14,
    lineHeight: TOKENS.lineHeight.s14,
    marginBottom: TOKENS.spacing.s4,
  },
  subviewContainer: {
    width: "100%",
    flex: 1,
    alignItems: "center",
  },
  backHeader: {
    width: "100%",
    alignItems: "flex-start",
    marginBottom: TOKENS.spacing.s8,
  },
  backLink: {
    borderRadius: TOKENS.radius.r8,
    paddingHorizontal: TOKENS.spacing.s8,
    paddingVertical: TOKENS.spacing.s4,
    marginBottom: TOKENS.spacing.s4,
  },
  backLinkFocused: {
    borderWidth: 2,
    borderColor: TOKENS.color.focusRing,
  },
  backLinkPressed: {
    opacity: 0.7,
  },
  backLinkText: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.s12,
    lineHeight: TOKENS.lineHeight.s12,
    color: TOKENS.color.textPrimary,
  },
  viewTitle: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.s14,
    lineHeight: TOKENS.lineHeight.s14,
    paddingHorizontal: TOKENS.spacing.s8,
  },
  sectionTitle: {
    width: "100%",
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.s12,
    lineHeight: TOKENS.lineHeight.s12,
    marginBottom: TOKENS.spacing.s6,
    paddingHorizontal: TOKENS.spacing.s8,
  },
  assetScroll: {
    width: "100%",
    flex: 1,
  },
  assetGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: TOKENS.spacing.s8,
    paddingBottom: TOKENS.spacing.s12,
    paddingHorizontal: TOKENS.spacing.s8,
    alignItems: "flex-start",
  },
  assetCard: {
    width: 88,
    height: 88,
    minWidth: 44,
    minHeight: 44,
    borderRadius: TOKENS.radius.r8,
    borderWidth: 1,
    borderColor: TOKENS.color.borderSubtle,
    overflow: "hidden",
    backgroundColor: TOKENS.color.surfaceMuted,
  },
  assetCardFocused: {
    borderWidth: 2,
    borderColor: TOKENS.color.focusRing,
  },
  assetCardDisabled: {
    opacity: 0.6,
  },
  assetImage: {
    width: "100%",
    height: "100%",
  },
  feedbackContainer: {
    alignItems: "center",
    gap: TOKENS.spacing.s8,
    paddingHorizontal: TOKENS.spacing.s12,
    width: "100%",
    flex: 1,
    justifyContent: "center",
  },
  errorText: {
    color: TOKENS.color.danger,
  },
  assetCardWrapper: {
    position: "relative",
  },
  deleteButton: {
    position: "absolute",
    top: 6,
    right: 6,
    zIndex: 10,
  },
  deleteIconContainer: {
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    borderRadius: 14,
    width: 28,
    height: 28,
    justifyContent: "center",
    alignItems: "center",
  },
  dialogOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  dialogContainer: {
    backgroundColor: TOKENS.color.inverseText,
    borderRadius: TOKENS.radius.r8,
    padding: TOKENS.spacing.s12,
    width: 320,
    gap: TOKENS.spacing.s12,
    alignItems: "center",
  },
  dialogTitle: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.s14,
    lineHeight: TOKENS.lineHeight.s14,
  },
  dialogImagePreview: {
    width: 120,
    height: 120,
    borderRadius: TOKENS.radius.r8,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: TOKENS.color.borderSubtle,
  },
  dialogImage: {
    width: "100%",
    height: "100%",
  },
  dialogMessage: {
    fontSize: TOKENS.fontSize.s12,
    lineHeight: TOKENS.lineHeight.s12,
    textAlign: "center",
  },
  dialogButtons: {
    flexDirection: "row",
    gap: TOKENS.spacing.s8,
    justifyContent: "center",
  },
});

export default AddImageModalContent;
